import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MenuRounded, SearchRounded } from '@mui/icons-material';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { PracticeSettingsHeading } from './PracticeSettingsHeading';

export interface PracticeSettingsItem {
  path: string;
  label: string;
  group: string;
  icon: ReactNode;
  badge?: number;
  subtitle?: string;
}

interface PracticeSettingsShellProps {
  items: PracticeSettingsItem[];
  activePath: string;
  title?: string;
  subtitle?: string;
  showPageHeading?: boolean;
  workspace?: boolean;
  onNavigate: (item: PracticeSettingsItem) => void;
  children: ReactNode;
}

/** Uses the Patient Page v3 rail's width, typography, colors and selection treatment. */
export function PracticeSettingsShell({
  items,
  activePath,
  title,
  subtitle,
  showPageHeading = true,
  workspace = false,
  onNavigate,
  children,
}: PracticeSettingsShellProps) {
  const desktopNavRef = useRef<HTMLDivElement>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const filtered = items.filter(item =>
    `${item.label} ${item.group}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const groups = [...new Set(filtered.map(item => item.group))];
  const active = items.find(item => item.path === activePath);
  useEffect(() => {
    for (const list of [desktopNavRef.current, mobileNavRef.current]) {
      const selected = list?.querySelector<HTMLElement>('[aria-current="page"]');
      if (!list || !selected) continue;
      const itemBounds = selected.getBoundingClientRect();
      const listBounds = list.getBoundingClientRect();
      const delta =
        itemBounds.top < listBounds.top
          ? itemBounds.top - listBounds.top
          : itemBounds.bottom > listBounds.bottom
            ? itemBounds.bottom - listBounds.bottom
            : 0;
      if (delta) list.scrollTo({ top: list.scrollTop + delta });
    }
  }, [activePath, query, mobileOpen]);

  const rail = (navRef: RefObject<HTMLDivElement | null>) => (
    <Stack sx={{ height: '100%', bgcolor: 'grey.50', minHeight: 0 }}>
      <Box sx={{ px: 1.5, pt: 2.25, pb: 1.25, flexShrink: 0 }}>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 10,
            fontWeight: 850,
            letterSpacing: '0.1em',
            mb: 1,
            px: 0.5,
          }}
        >
          SETTINGS
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 32,
            px: 1,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            '&:focus-within': { borderColor: 'primary.main' },
          }}
        >
          <SearchRounded sx={{ color: 'text.disabled', fontSize: 16, mr: 0.75 }} />
          <InputBase
            fullWidth
            placeholder="Find a setting…"
            value={query}
            onChange={event => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Find a setting' }}
            sx={{ fontSize: 12.5 }}
          />
        </Box>
      </Box>
      <Box
        component="nav"
        ref={navRef}
        aria-label="Practice settings"
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 2 }}
      >
        {groups.map(group => (
          <Box key={group} sx={{ mb: 1.5 }}>
            <Typography
              sx={{
                px: 1.25,
                pt: 1.5,
                pb: 0.75,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {group}
            </Typography>
            <List dense disablePadding>
              {filtered
                .filter(item => item.group === group)
                .map(item => (
                  <ListItemButton
                    key={item.path}
                    component={Link}
                    to={item.path}
                    selected={item.path === activePath}
                    aria-current={item.path === activePath ? 'page' : undefined}
                    onClick={() => {
                      onNavigate(item);
                      setMobileOpen(false);
                    }}
                    sx={{
                      minHeight: 40,
                      borderRadius: 0,
                      px: 1.25,
                      color: item.path === activePath ? 'primary.dark' : 'text.secondary',
                      '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          sx: { fontSize: 12.5, fontWeight: item.path === activePath ? 760 : 620 },
                        },
                      }}
                    />
                    {!!item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{ height: 20, fontSize: 10, bgcolor: 'background.paper' }}
                      />
                    )}
                  </ListItemButton>
                ))}
            </List>
          </Box>
        ))}
        {!filtered.length && (
          <Typography
            role="status"
            sx={{ px: 1.25, py: 2.5, fontSize: 12.5, color: 'text.secondary' }}
          >
            No settings match
          </Typography>
        )}
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
        <Box
          component="aside"
          sx={{
            display: { xs: 'none', md: 'block' },
            // The rail disappears on mobile; it must not anchor the page scroll.
            overflowAnchor: 'none',
            width: 232,
            flex: '0 0 232px',
            bgcolor: 'grey.50',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              height: 'var(--app-content-height, 100dvh)',
            }}
          >
            {rail(desktopNavRef)}
          </Box>
        </Box>
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { md: 'none' }, '& .MuiDrawer-paper': { width: 276, borderRadius: 0 } }}
        >
          {rail(mobileNavRef)}
        </Drawer>
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2, lg: 4 },
            py: 3,
            ...(workspace && {
              height: 'var(--app-content-height, 100dvh)',
              boxSizing: 'border-box',
            }),
          }}
        >
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 2, flexShrink: 0 }}>
            <IconButton aria-label="Open settings navigation" onClick={() => setMobileOpen(true)}>
              <MenuRounded />
            </IconButton>
          </Box>
          <Box
            sx={{
              maxWidth: 1200,
              mx: 'auto',
              width: '100%',
              ...(workspace && { flex: 1, minHeight: 0 }),
            }}
          >
            {showPageHeading && (
              <PracticeSettingsHeading
                title={title ?? active?.label ?? 'Settings'}
                subtitle={subtitle ?? active?.subtitle}
              />
            )}
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
